import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Avatar,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CampaignIcon from '@mui/icons-material/Campaign';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StraightenIcon from '@mui/icons-material/Straighten';
import TimerIcon from '@mui/icons-material/Timer';
import PlaceIcon from '@mui/icons-material/Place';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import api from '../api/client';
import EmptyState from '../components/domain/EmptyState';
import SegmentedControl from '../components/domain/SegmentedControl';
import WorkoutRouteMap from '../components/domain/WorkoutRouteMap';
import { getWorkoutTypeMeta } from '../utils/workoutTypes';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';

const DEPARTMENTS = [
  'HR', 'IT', 'Sprzedaż', 'Marketing', 'Operacje', 'Finanse', 'Prawo', 'Produkcja', 'Inne',
];

const ROLE_LABELS = {
  admin: 'Administrator',
  hr: 'HR',
  employee: 'Pracownik',
};

const STATUS_LABELS = {
  active: 'Aktywny',
  inactive: 'Nieaktywny',
};

function formatPhone(value) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return `+${digits}`;
  const country = digits.slice(0, 2);
  const rest = digits.slice(2);
  const parts = rest.match(/.{1,3}/g) || [];
  return `+${country} ${parts.join(' ')}`.trim();
}

function parsePhone(value) {
  return value.replace(/\D/g, '');
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');
}

function MetricBarChart({ data = [], color, unit, height = 220 }) {
  const theme = useTheme();
  const hasData = data.length > 0 && data.some((d) => d.value > 0);

  if (!hasData) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">Brak danych</Typography>
      </Box>
    );
  }

  const avg = data.reduce((s, d) => s + d.value, 0) / data.length;
  const chartData = data.map((d) => ({ ...d, avg }));

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
          <XAxis
            dataKey="label"
            tick={{ fill: theme.palette.text.secondary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            angle={data.length > 14 ? -35 : 0}
            height={data.length > 14 ? 50 : 30}
            textAnchor={data.length > 14 ? 'end' : 'middle'}
          />
          <YAxis
            tick={{ fill: theme.palette.text.secondary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={50}
            tickFormatter={(v) => Math.round(v).toLocaleString('pl-PL')}
            label={{ value: unit, angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary, fontSize: 10, dx: -5 }}
          />
          <Tooltip
            cursor={{ fill: theme.palette.action.hover }}
            contentStyle={{
              borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              fontSize: 12,
            }}
            formatter={(value) => [Math.round(Number(value)).toLocaleString('pl-PL'), unit]}
            labelFormatter={(label) => label}
          />
          <ReferenceLine
            y={avg}
            stroke={theme.palette.text.secondary}
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{ value: 'średnia', position: 'right', fill: theme.palette.text.secondary, fontSize: 10 }}
          />
          <Bar dataKey="value" radius={[8, 8, 0, 0]} animationDuration={600}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={color} fillOpacity={0.95} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [employee, setEmployee] = useState(null);
  const [summary, setSummary] = useState({ totals: { steps: 0, distance: '0 m', distanceMeters: 0, activeEnergy: 0 }, totalPoints: 0, campaignsCount: 0, activeCampaigns: 0 });
  const [activities, setActivities] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [activityRange, setActivityRange] = useState('week');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resetDialog, setResetDialog] = useState({ open: false, link: '' });
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    displayName: '',
    privateEmail: '',
    phone: '',
    employeeId: '',
    position: '',
    department: '',
    team: '',
    activeFrom: '',
    activeUntil: '',
    indefinite: true,
    gdprConsent: false,
    marketingConsent: false,
    termsConsent: false,
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userData, summaryData, activitiesData, emailsData] = await Promise.all([
        api.get(`/api/tenants/me/users/${id}`),
        api.get(`/api/tenants/me/users/${id}/summary`),
        api.get(`/api/tenants/me/users/${id}/activities`),
        api.get(`/api/tenants/me/users/${id}/emails`).catch(() => []),
      ]);
      setEmployee(userData);
      setSummary(summaryData);
      setActivities(Array.isArray(activitiesData) ? activitiesData : activitiesData.activities || []);
      setEmails(Array.isArray(emailsData) ? emailsData : emailsData.emails || []);
      setForm({
        displayName: userData.display_name || '',
        privateEmail: userData.private_email || '',
        phone: formatPhone(userData.phone),
        employeeId: userData.employee_id || '',
        position: userData.position || '',
        department: userData.department || '',
        team: userData.team || '',
        activeFrom: userData.active_from ? userData.active_from.slice(0, 10) : '',
        activeUntil: userData.active_until ? userData.active_until.slice(0, 10) : '',
        indefinite: !userData.active_until,
        gdprConsent: !!userData.gdpr_consent_at,
        marketingConsent: !!userData.marketing_consent_at,
        termsConsent: !!userData.terms_consent_at,
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.body?.error || err.message || 'Błąd pobierania danych', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (form.privateEmail && !isValidEmail(form.privateEmail)) {
      nextErrors.privateEmail = 'Nieprawidłowy adres e-mail';
    }
    const rawPhone = parsePhone(form.phone);
    if (form.phone && rawPhone.length < 6) {
      nextErrors.phone = 'Nieprawidłowy numer telefonu';
    }
    if (form.activeFrom && form.activeUntil && !form.indefinite && form.activeUntil < form.activeFrom) {
      nextErrors.activeUntil = 'Data końcowa nie może być wcześniejsza niż początkowa';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        privateEmail: form.privateEmail.trim() || null,
        phone: parsePhone(form.phone) || null,
        employeeId: form.employeeId.trim() || null,
        position: form.position.trim() || null,
        department: form.department || null,
        team: form.team.trim() || null,
        activeFrom: form.activeFrom || null,
        activeUntil: form.indefinite ? null : (form.activeUntil || null),
        indefinite: form.indefinite,
        gdprConsent: form.gdprConsent,
        marketingConsent: form.marketingConsent,
        termsConsent: form.termsConsent,
      };
      await api.put(`/api/tenants/me/users/${id}`, payload);
      await loadData();
      setSnackbar({ open: true, message: 'Dane zapisane.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.body?.error || err.message || 'Błąd zapisywania', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    const nextStatus = employee.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/tenants/me/users/${id}`, { status: nextStatus });
      await loadData();
      setSnackbar({ open: true, message: nextStatus === 'active' ? 'Konto aktywowane.' : 'Konto dezaktywowane.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.body?.error || err.message || 'Błąd zmiany statusu', severity: 'error' });
    }
  };

  const sendPasswordReset = async () => {
    try {
      const res = await api.post('/api/auth/forgot-password', { email: employee.email });
      if (res.link) {
        setResetDialog({ open: true, link: res.link });
      }
      setSnackbar({ open: true, message: 'Wysłano e-mail z linkiem do resetu hasła.', severity: 'success' });
      await loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.body?.error || err.message || 'Błąd wysyłania resetu hasła', severity: 'error' });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pl-PL');
  };

  const formatMetric = (activity) => {
    let metrics = activity.metrics;
    if (typeof metrics === 'string') {
      try {
        metrics = JSON.parse(metrics);
      } catch {
        metrics = {};
      }
    }
    if (!metrics || metrics.value == null) {
      return activity.points != null ? `${activity.points} pkt` : '—';
    }
    const value = Number(metrics.value);
    const type = activity.type;
    if (type === 'STEPS') return `${value.toLocaleString('pl-PL')} kroków`;
    if (type === 'DISTANCE') {
      if (value >= 1000) return `${(value / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} km`;
      return `${value.toLocaleString('pl-PL')} m`;
    }
    if (type === 'ACTIVE_ENERGY') return `${value.toLocaleString('pl-PL')} kcal`;
    if (type === 'WORKOUT') {
      const duration = Number(metrics.durationMinutes ?? metrics.value) || 0;
      const parts = [`${Math.round(duration)} min`];
      if (metrics.calories) parts.push(`${Math.round(Number(metrics.calories))} kcal`);
      if (metrics.distanceMeters) parts.push(`${(Number(metrics.distanceMeters) / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} km`);
      return parts.join(' • ');
    }
    return `${value.toLocaleString('pl-PL')} ${metrics.unit || ''}`.trim();
  };

  const formatType = (type, activity) => {
    switch (type) {
      case 'STEPS': return 'Kroki';
      case 'DISTANCE': return 'Dystans';
      case 'ACTIVE_ENERGY': return 'Energia';
      case 'WORKOUT': {
        let metrics = activity?.metrics;
        if (typeof metrics === 'string') {
          try { metrics = JSON.parse(metrics); } catch { metrics = {}; }
        }
        return getWorkoutTypeMeta(metrics?.workoutType).label;
      }
      default: return type;
    }
  };

  const parseMetricValue = (activity, type) => {
    let metrics = activity.metrics;
    if (typeof metrics === 'string') {
      try { metrics = JSON.parse(metrics); } catch { metrics = {}; }
    }
    if (type === 'WORKOUT') return Number(metrics?.durationMinutes ?? metrics?.value) || 0;
    const value = Number(metrics?.value) || 0;
    if (type === 'DISTANCE') return value / 1000;
    return value;
  };

  const toLocalDateString = (dateInput) => {
    const d = new Date(dateInput);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const startOfWeek = (date) => {
    const d = startOfDay(date);
    const day = d.getDay() || 7;
    if (day !== 1) d.setDate(d.getDate() - day + 1);
    return d;
  };

  const formatLabel = (date, range) => {
    if (range === 'week' || range === 'month') {
      return date.toLocaleDateString('pl-PL', { weekday: range === 'week' ? 'short' : undefined, day: 'numeric', month: 'numeric' });
    }
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'numeric' });
  };

  const buildMetricData = (type, range) => {
    const now = new Date();
    const days = range === 'week' ? 7 : range === 'month' ? 30 : 180;
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - days + 1);
    cutoff.setHours(0, 0, 0, 0);

    const filtered = activities.filter((a) => a.type === type && new Date(a.timestamp) >= cutoff);
    const filteredByDay = filtered.map((a) => ({ activity: a, day: toLocalDateString(a.timestamp) }));

    if (range === 'halfYear') {
      const buckets = [];
      const currentWeek = startOfWeek(now);
      for (let i = 25; i >= 0; i--) {
        const weekStart = new Date(currentWeek);
        weekStart.setDate(weekStart.getDate() - i * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const startDay = toLocalDateString(weekStart);
        const endDay = toLocalDateString(weekEnd);
        const sum = filteredByDay
          .filter(({ day }) => day >= startDay && day <= endDay)
          .reduce((acc, { activity }) => acc + parseMetricValue(activity, type), 0);
        buckets.push({ label: formatLabel(weekStart, range), value: sum });
      }
      return buckets;
    }

    const buckets = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayKey = toLocalDateString(d);
      const sum = filteredByDay
        .filter(({ day }) => day === dayKey)
        .reduce((acc, { activity }) => acc + parseMetricValue(activity, type), 0);
      buckets.push({ label: formatLabel(d, range), value: sum });
    }
    return buckets;
  };

  const getBackPath = () => location.pathname.replace(/\/employees\/.*/, '/employees');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <Typography color="text.secondary">Ładowanie...</Typography>
      </Box>
    );
  }

  if (!employee) {
    return (
      <EmptyState
        icon={<PersonIcon sx={{ fontSize: 32 }} />}
        title="Nie znaleziono pracownika"
        description="Pracownik o podanym identyfikatorze nie istnieje."
        actionLabel="Wróć do listy"
        onAction={() => navigate(getBackPath())}
      />
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(getBackPath())} sx={{ mb: 2 }}>
        Pracownicy
      </Button>

      <Card sx={{ mb: 3, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.light', color: 'primary.main', fontSize: '1.5rem', fontWeight: 700 }}>
              {(form.displayName || employee.email).split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 750, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {form.displayName || employee.email}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {employee.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={ROLE_LABELS[employee.role] || employee.role} size="small" color="primary" />
                <Chip label={STATUS_LABELS[employee.status] || employee.status} size="small" color={employee.status === 'active' ? 'success' : 'default'} />
                <Chip label={`Dołączył: ${formatDate(employee.created_at)}`} size="small" variant="outlined" />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<LockResetIcon />}
                onClick={sendPasswordReset}
                sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
              >
                Reset hasła
              </Button>
              <Button
                variant="outlined"
                color={employee.status === 'active' ? 'error' : 'success'}
                startIcon={employee.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />}
                onClick={toggleStatus}
                sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
              >
                {employee.status === 'active' ? 'Dezaktywuj' : 'Aktywuj'}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {[
              { label: 'Kroki', value: (summary.totals?.steps ?? 0).toLocaleString('pl-PL'), icon: <DirectionsRunIcon /> },
              { label: 'Dystans', value: summary.totals?.distance ?? '0 m', icon: <EmojiEventsIcon /> },
              { label: 'Energia', value: `${(summary.totals?.activeEnergy ?? 0).toLocaleString('pl-PL')} kcal`, icon: <CampaignIcon /> },
              { label: 'Aktywności', value: activities.length, icon: <DirectionsRunIcon /> },
            ].map((m) => (
              <Grid item xs={6} md={3} key={m.label}>
                <Box sx={{ p: 2, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.tokens?.semantic?.surface?.brandTint, color: 'primary.main' }}>{m.icon}</Avatar>
                    <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px solid ${theme.palette.divider}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 2 }}>
          <Tab label="Dane i kontakt" />
          <Tab label="Zgody" />
          <Tab label={`E-maile (${emails.length})`} />
          <Tab label={`Aktywności (${activities.length})`} />
        </Tabs>

        {tab === 0 && (
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField label="E-mail służbowy" fullWidth value={employee.email} disabled helperText="Wymagany, nieedytowalny" sx={{ mb: 2 }} />
                <TextField
                  label="Imię i nazwisko"
                  fullWidth
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="E-mail prywatny"
                  fullWidth
                  value={form.privateEmail}
                  onChange={(e) => setForm((f) => ({ ...f, privateEmail: e.target.value }))}
                  error={!!errors.privateEmail}
                  helperText={errors.privateEmail || 'Opcjonalny, ale wymagany jeśli brak służbowego'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Telefon"
                  fullWidth
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                  error={!!errors.phone}
                  helperText={errors.phone || 'Format: +48 123 456 789'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="ID pracownika"
                  fullWidth
                  value={form.employeeId}
                  onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Stanowisko"
                  fullWidth
                  value={form.position}
                  onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="department-label">Dział</InputLabel>
                  <Select
                    labelId="department-label"
                    value={form.department}
                    label="Dział"
                    onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                  >
                    <MenuItem value=""><em>Brak</em></MenuItem>
                    {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  label="Zespół"
                  fullWidth
                  value={form.team}
                  onChange={(e) => setForm((f) => ({ ...f, team: e.target.value }))}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Okres aktywności konta</Typography>
                <FormControlLabel
                  control={<Switch checked={form.indefinite} onChange={(e) => setForm((f) => ({ ...f, indefinite: e.target.checked }))} />}
                  label="Konto bezterminowe"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Aktywne od"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.activeFrom}
                    onChange={(e) => setForm((f) => ({ ...f, activeFrom: e.target.value }))}
                  />
                  <TextField
                    label="Aktywne do"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.activeUntil}
                    onChange={(e) => setForm((f) => ({ ...f, activeUntil: e.target.value }))}
                    disabled={form.indefinite}
                    error={!!errors.activeUntil}
                    helperText={errors.activeUntil}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
                  >
                    {saving ? 'Zapisywanie...' : 'Zapisz dane'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {tab === 1 && (
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <GavelIcon /> Zgody na przetwarzanie danych
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <FormControlLabel
                control={<Switch checked={form.gdprConsent} onChange={(e) => setForm((f) => ({ ...f, gdprConsent: e.target.checked }))} />}
                label={`Zgoda RODO (GDPR) — ${employee.gdpr_consent_at ? 'Potwierdzona ' + formatDate(employee.gdpr_consent_at) : 'niepotwierdzona'}`}
              />
              <FormControlLabel
                control={<Switch checked={form.marketingConsent} onChange={(e) => setForm((f) => ({ ...f, marketingConsent: e.target.checked }))} />}
                label={`Zgoda marketingowa — ${employee.marketing_consent_at ? 'Potwierdzona ' + formatDate(employee.marketing_consent_at) : 'niepotwierdzona'}`}
              />
              <FormControlLabel
                control={<Switch checked={form.termsConsent} onChange={(e) => setForm((f) => ({ ...f, termsConsent: e.target.checked }))} />}
                label={`Regulamin — ${employee.terms_consent_at ? 'Potwierdzony ' + formatDate(employee.terms_consent_at) : 'niepotwierdzony'}`}
              />
            </Box>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
              {saving ? 'Zapisywanie...' : 'Zapisz zgody'}
            </Button>
          </CardContent>
        )}

        {tab === 2 && (
          <CardContent>
            {emails.length === 0 ? (
              <EmptyState icon={<EmailIcon sx={{ fontSize: 32 }} />} title="Brak wysłanych e-maili" description="Do tego pracownika nie zostały jeszcze wysłane żadne komunikaty." />
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Odbiorca</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emails.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>{formatDate(e.sent_at)}</TableCell>
                        <TableCell>{e.email_type}</TableCell>
                        <TableCell>{e.recipient}</TableCell>
                        <TableCell>
                          <Chip label={e.status} size="small" color={e.status === 'sent' ? 'success' : e.status === 'failed' ? 'error' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}

        {tab === 3 && (
          <CardContent>
            {activities.length === 0 ? (
              <EmptyState icon={<DirectionsRunIcon sx={{ fontSize: 32 }} />} title="Brak aktywności" description="Ten pracownik nie ma jeszcze zapisanych aktywności." />
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Aktywności pracownika
                  </Typography>
                  <SegmentedControl
                    options={[
                      { value: 'week', label: 'Tydzień' },
                      { value: 'month', label: 'Miesiąc' },
                      { value: 'halfYear', label: 'Pół roku' },
                    ]}
                    value={activityRange}
                    onChange={setActivityRange}
                  />
                </Box>

                {[
                  { type: 'STEPS', label: 'Kroki', unit: 'kroków', color: theme.tokens?.primitive?.color?.brand?.[600] ?? '#5840DB' },
                  { type: 'DISTANCE', label: 'Dystans', unit: 'km', color: theme.tokens?.primitive?.color?.semantic?.positive ?? '#17845B' },
                  { type: 'ACTIVE_ENERGY', label: 'Energia', unit: 'kcal', color: theme.tokens?.primitive?.color?.semantic?.warning ?? '#A96400' },
                  { type: 'WORKOUT', label: 'Treningi (minuty)', unit: 'min', color: theme.tokens?.primitive?.color?.semantic?.info ?? '#286DB5' },
                ].map((m) => {
                  const data = buildMetricData(m.type, activityRange);
                  const avg = data.length ? data.reduce((s, d) => s + d.value, 0) / data.length : 0;
                  return (
                    <Card
                      key={m.type}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{m.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Średnia: {avg > 0 ? `${Math.round(avg).toLocaleString('pl-PL')} ${m.unit}` : '—'}
                        </Typography>
                      </Box>
                      <MetricBarChart data={data} color={m.color} unit={m.unit} height={160} />
                    </Card>
                  );
                })}

                {(() => {
                  const now = new Date();
                  const days = activityRange === 'week' ? 7 : activityRange === 'month' ? 30 : 180;
                  const cutoff = new Date(now);
                  cutoff.setDate(cutoff.getDate() - days + 1);
                  cutoff.setHours(0, 0, 0, 0);

                  const workouts = activities
                    .filter((a) => a.type === 'WORKOUT' && new Date(a.timestamp) >= cutoff)
                    .map((a) => {
                      let metrics = a.metrics;
                      if (typeof metrics === 'string') {
                        try { metrics = JSON.parse(metrics); } catch { metrics = {}; }
                      }
                      return { activity: a, metrics: metrics || {} };
                    })
                    .sort((a, b) => new Date(b.activity.timestamp) - new Date(a.activity.timestamp));

                  if (workouts.length === 0) return null;

                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Treningi ({workouts.length})
                      </Typography>
                      {workouts.map(({ activity, metrics }) => {
                        const { label, Icon } = getWorkoutTypeMeta(metrics.workoutType);
                        const duration = Math.round(Number(metrics.durationMinutes ?? metrics.value) || 0);
                        const calories = metrics.calories ? Math.round(Number(metrics.calories)) : null;
                        const distanceKm = metrics.distanceMeters
                          ? (Number(metrics.distanceMeters) / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 2 })
                          : null;
                        const route = Array.isArray(metrics.route) ? metrics.route : null;

                        return (
                          <Accordion
                            key={activity.id}
                            sx={{
                              mb: 1,
                              borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px !important`,
                              border: `1px solid ${theme.palette.divider}`,
                              boxShadow: 'none',
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                  <Icon fontSize="small" />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(activity.timestamp)} • {activity.source}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  <Chip size="small" icon={<TimerIcon />} label={`${duration} min`} />
                                  {calories != null && <Chip size="small" icon={<LocalFireDepartmentIcon />} label={`${calories} kcal`} />}
                                  {distanceKm != null && <Chip size="small" icon={<StraightenIcon />} label={`${distanceKm} km`} />}
                                  {route && route.length > 1 && <Chip size="small" color="primary" variant="outlined" icon={<PlaceIcon />} label="GPS" />}
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {route && route.length > 1 ? (
                                <WorkoutRouteMap route={route} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Brak danych GPS dla tego treningu.
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  );
                })()}

                <TableContainer component={Paper} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, boxShadow: 'none', mt: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Typ</TableCell>
                        <TableCell>Źródło</TableCell>
                        <TableCell align="right">Wartość</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activities.map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell>{formatDate(a.timestamp)}</TableCell>
                          <TableCell>{formatType(a.type, a)}</TableCell>
                          <TableCell>{a.source}</TableCell>
                          <TableCell align="right">{formatMetric(a)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        )}
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={resetDialog.open} onClose={() => setResetDialog({ open: false, link: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Link do resetu hasła</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            W PoC e-mail jest logowany, a nie wysyłany przez zewnętrzny serwis. Przekaż link użytkownikowi:
          </Typography>
          <TextField fullWidth value={resetDialog.link} InputProps={{ readOnly: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { navigator.clipboard.writeText(resetDialog.link); setSnackbar({ open: true, message: 'Link skopiowany.', severity: 'success' }); }}>Kopiuj</Button>
          <Button onClick={() => setResetDialog({ open: false, link: '' })}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeDetail;
